import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Select, Space, Typography } from 'antd';
import { useGlobalContext } from '@contexts/global';
import { CommonUtility, ManifestService, ToastMessage } from '@utility/index';
import type { ManifestCarrier } from 'types/manifest';

const { Text, Title } = Typography;

interface ManifestReportFormValues {
  carrier: string;
}

export function ManifestReportScreen(): JSX.Element {
  const [form] = Form.useForm<ManifestReportFormValues>();
  const { warehouseId, warehouseLoading, currentWarehouse } = useGlobalContext();
  const [carriers, setCarriers] = useState<ManifestCarrier[]>([]);
  const [carriersLoading, setCarriersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const selectedCarrier = Form.useWatch('carrier', form);

  useEffect(() => {
    let mounted = true;

    const loadCarriers = async () => {
      form.resetFields(['carrier']);

      if (!warehouseId) {
        setCarriers([]);
        return;
      }

      setCarriersLoading(true);
      try {
        const response = await ManifestService.getCarriers({ warehouseId });
        if (mounted) {
          setCarriers(response.data ?? []);
        }
      } catch (error) {
        ToastMessage.apiError(error);
      } finally {
        if (mounted) {
          setCarriersLoading(false);
        }
      }
    };

    loadCarriers();

    return () => {
      mounted = false;
    };
  }, [form, warehouseId]);

  const carrierOptions = useMemo(
    () =>
      carriers.map(carrier => ({
        value: carrier.value,
        label: carrier.name,
      })),
    [carriers],
  );

  const handleGenerate = async ({ carrier }: ManifestReportFormValues) => {
    if (!warehouseId) {
      ToastMessage.error('Select a warehouse before generating a manifest report.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await ManifestService.generateReport({
        warehouseId,
        carrier,
      });

      if (!response.data?.url) {
        ToastMessage.error('Manifest report URL was not returned.');
        return;
      }

      const fileResponse = await fetch(response.data.url);
      const fileBlob = await fileResponse.blob();
      CommonUtility.downloadPDF(
        fileBlob,
        response.data.file_name || 'manifest-report.pdf',
      );
      ToastMessage.success('Manifest report downloaded successfully.');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const disabledReason = useMemo(() => {
    if (warehouseLoading) return 'Loading selected warehouse...';
    if (!warehouseId) return 'Select a warehouse before generating a report.';
    if (!selectedCarrier) return 'Select a carrier to enable report generation.';
    return '';
  }, [selectedCarrier, warehouseId, warehouseLoading]);

  return (
    <Card style={{ maxWidth: 640 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 8 }}>
            Manifest Report
          </Title>
          <Text type="secondary">
            Generate a manifest report for the currently selected warehouse
            {currentWarehouse?.name ? `: ${currentWarehouse.name}` : ''}.
          </Text>
        </div>

        <Form form={form} layout="vertical" onFinish={handleGenerate}>
          <Form.Item
            name="carrier"
            label="Carrier"
            rules={[{ required: true, message: 'Select a carrier' }]}
          >
            <Select
              placeholder="Select carrier"
              loading={carriersLoading}
              disabled={!warehouseId || carriersLoading || submitting}
              options={carrierOptions}
            />
          </Form.Item>

          {disabledReason && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {disabledReason}
            </Text>
          )}

          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            disabled={Boolean(disabledReason) || carriersLoading}
          >
            Generate Report
          </Button>
        </Form>
      </Space>
    </Card>
  );
}
